import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerWelcome from "@/pages/CustomerWelcome";
import CustomerReferenceForm from "@/pages/CustomerReferenceForm";
import ReferencePortal from "@/pages/ReferencePortal";
import CrmDashboard from "@/pages/CrmDashboard";
import CrmCustomerProfile from "@/pages/CrmCustomerProfile";
import CrmPipeline from "@/pages/CrmPipeline";
import CrmActivityReport from "@/pages/CrmActivityReport";
import CrmMyDay from "@/pages/CrmMyDay";
import SalesPortal from "@/pages/SalesPortal";
import ServicePortal from "@/pages/ServicePortal";
import UsersAdmin from "@/pages/UsersAdmin";
import ReportsAdmin from "@/pages/ReportsAdmin";
import PurchasingPortal from "@/pages/PurchasingPortal";
import ChecklistsAdmin from "@/pages/ChecklistsAdmin";
import InstrumentationAdmin from "@/pages/InstrumentationAdmin";
import InstrumentationPortal from "@/pages/InstrumentationPortal";
import MobileCrm from "@/pages/mobile/MobileCrm";
import { MobileVersionBanner } from "@/components/MobileVersionBanner";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  const to = user.role === "customer" ? "/welcome" : user.role === "instrument_customer" ? "/instrumentation/portal" : "/crm";
  return <Navigate to={to} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reference/:token" element={<ReferencePortal />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}>
                  <CrmDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/pipeline"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}>
                  <CrmPipeline />
                </ProtectedRoute>
              }
            />
            <Route path="/crm/activity" element={<ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}><CrmActivityReport /></ProtectedRoute>} />
            <Route path="/crm/my-day" element={<ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}><CrmMyDay /></ProtectedRoute>} />
            <Route
              path="/crm/customers/:id"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}>
                  <CrmCustomerProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/welcome"
              element={
                <ProtectedRoute role="customer">
                  <CustomerWelcome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reference-form"
              element={
                <ProtectedRoute role="customer">
                  <CustomerReferenceForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales_manager"]}>
                  <SalesPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/service"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}>
                  <ServicePortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales_manager"]}>
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales_manager"]}>
                  <ReportsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchasing"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales_manager"]}>
                  <PurchasingPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklists"
              element={
                <ProtectedRoute roles={["admin", "manager", "sales_manager"]}>
                  <ChecklistsAdmin />
                </ProtectedRoute>
              }
            />
            <Route path="/instrumentation" element={<ProtectedRoute roles={["admin", "manager", "sales_manager"]}><InstrumentationAdmin /></ProtectedRoute>} />
            <Route path="/instrumentation/portal" element={<ProtectedRoute role="instrument_customer"><InstrumentationPortal /></ProtectedRoute>} />
            <Route path="/mobilecrm/*" element={<ProtectedRoute roles={["admin", "manager", "sales", "sales_manager"]}><MobileCrm /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <MobileVersionBanner />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
